import React, { useEffect, useRef } from "react";
import '../../style/about.css';
import '../../style/modal.css';
import '../../style/utils.css';
import '../../style/actionImageCard.css';
import '../../style/base.css';
import '../../style/list.css';
import LeadForm from "../leadForm";
import { useNavigate } from "react-router-dom";
import ApiClient, { getImageUrl } from '../../Services/ApiClient';
import ActionImageCard from '../actionImageCard';
import '../../style/items.css';
import { useResources, useComponentResources } from '../../store/resourcesProvider';
import { useAlert } from '../ui/alertProvider';
import { Item } from '../../../Models/Item';

function Items() {

    const [showLeadForm, setShowLeadForm] = React.useState(false);
    const [goToItemId, setGoToItemId] = React.useState<number | null>(null);
    const [items, setItems] = React.useState<Item[]>([]);
    const [isItems, setIsItems] = React.useState(false);
    const [page, setPage] = React.useState<number>(1);
    // responsive page size: 1 on mobile, 2 on desktop
    const [isMobile, setIsMobile] = React.useState<boolean>(() => {
        try { return typeof window !== 'undefined' ? window.innerWidth < 800 : false; } catch { return false; }
    });
    const pageSizeRef = React.useRef<number>(isMobile ? 1 : 2);
    const [hasMore, setHasMore] = React.useState<boolean>(true);
    const [loading, setLoading] = React.useState<boolean>(false);
    // refs to keep latest values for observer callback (avoid stale closures)
    const pageRef = React.useRef<number>(page);
    const loadingRef = React.useRef<boolean>(loading);
    const hasMoreRef = React.useRef<boolean>(hasMore);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const sentinelRef = React.useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const resources = useResources();
    const pageRes = useComponentResources('Items', ['Items.Title','Items.NoMore']);
    const alert = useAlert();
    
    React.useEffect(() => {

        const onResize = () => {
            try {
                const mobile = window.innerWidth < 800;
                if (mobile !== isMobile) {
                    setIsMobile(mobile);
                }
                pageSizeRef.current = mobile ? 1 : 2;
            } catch { }
        };
        window.addEventListener('resize', onResize);

        // run resize handler once to initialize pageSizeRef correctly for current viewport
        try { onResize(); } catch { }

        // initial load
        loadPage(1);

        // determine root for IntersectionObserver: prefer a scrollable container if present
        const container = containerRef.current;
        const sentinel = sentinelRef.current;
        let rootForObserver: Element | Document | null = null;
        try {
            if (container && sentinel) {
                const style = getComputedStyle(container);
                // only use the container as the observer root if it is scrollable
                // and the sentinel element is contained within it. If the sentinel
                // is outside the container, using the container as root will prevent
                // intersection events from firing (common on tablet layouts).
                if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && container.contains(sentinel)) {
                    rootForObserver = container;
                }
            }
        } catch (e) {
            rootForObserver = null;
        }
        if (!sentinel) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && hasMoreRef.current && !loadingRef.current) {
                    loadPage(pageRef.current + 1);
                }
            });
        }, { root: rootForObserver, rootMargin: '200px', threshold: 0.1 });
        observer.observe(sentinel);

        // fallback: also attach a scroll listener to aggressively detect near-bottom when observer misses
        const onScroll = () => {
            try {
                const rootEl = (rootForObserver as Element) || document.documentElement || document.body;
                const scrollTop = (rootEl as Element).scrollTop ?? window.scrollY;
                const clientHeight = (rootForObserver as Element)?.clientHeight ?? window.innerHeight;
                const scrollHeight = (rootForObserver as Element)?.scrollHeight ?? document.documentElement.scrollHeight;
                const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
                if (distanceToBottom < 300 && hasMoreRef.current && !loadingRef.current) {
                    loadPage(pageRef.current + 1);
                }
            } catch (e) {
                // ignore
            }
        };

        const scrollTarget: any = (rootForObserver as Element) || window;
        scrollTarget.addEventListener('scroll', onScroll, { passive: true });

        return () => {
            observer.disconnect();
            try { scrollTarget.removeEventListener('scroll', onScroll as EventListener); } catch { }
            window.removeEventListener('resize', onResize);
        };
    }, []);
    // eslint-disable-line react-hooks/exhaustive-deps
    const openLeadForm = (itemId: number) => {
        setShowLeadForm(true);
        setGoToItemId(itemId);
    }

    const closeModalAndGoToItemPage = (itemId: number | null) => {
        setShowLeadForm(false);
        if (itemId) navigate('/item/' + itemId);
    }

    const handleClickToItemPage = (itemId: number) => {
        // open lead form first, then navigate
        openLeadForm(itemId);
    };

    async function loadPage(p: number) {
        if (loadingRef.current) return;
        setLoading(true);
        loadingRef.current = true;
        try {
            const ps = pageSizeRef.current || 2;
            try { console.debug('[Items] loadPage', p, 'pageSize', ps); } catch {}
            const data: any = await ApiClient.get(`/item/paged/bytype?itemTypeString=${encodeURIComponent('1')}&page=${p}&pageSize=${ps}`);
            const fetched = data?.items || data?.Items || [];
            try { console.debug('[Items] fetched', fetched?.length, 'total?', data?.totalCount ?? data?.TotalCount); } catch {}
            if (fetched.length > 0) {
                if (p === 1) setItems(fetched);
                else setItems(prev => [...prev, ...fetched]);
                setIsItems(true);
                setPage(p);
                pageRef.current = p;
                const total = data?.totalCount ?? data?.TotalCount ?? 0;
                const currentCount = (p - 1) * ps + fetched.length;
                if (currentCount >= total) {
                    setHasMore(false);
                    hasMoreRef.current = false;
                } else {
                    setHasMore(true);
                    hasMoreRef.current = true;
                }
            } else {
                if (p === 1) setItems([]);
                setHasMore(false);
                hasMoreRef.current = false;
            }
        } catch (err) {
            console.error('Failed to load items', err);
            try { alert.showAlert('Failed to load items: ' + ((err as any)?.message || String(err)), 'error'); } catch {}
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }

    

    return (
        <div>
            <div className="divItems page-padding">
                {pageRes['Items.Title'] ?? resources.get('Items.Title', 'מאמרים')}
            </div>
            <div className="table-container" ref={containerRef}>
                <div className="items-grid">
                    {items.map((item, idx) => (
                        // Ensure each item occupies its own grid cell so rows remain consistent while paginating
                        <div key={item.id} className="grid-item" data-idx={idx}>
                            <ActionImageCard
                                imageUrl={getImageUrl(item.imageFileName || item.imageName)}
                                title={item.title}
                                actionLabel={resources.get('Action.MoreInfo','מידע נוסף')}
                                onAction={() => handleClickToItemPage(item.id)}
                            />
                        </div>
                    ))}
                </div>
            </div>
            <div id="items-sentinel" className="items-sentinel" ref={sentinelRef} />
            {loading && (
                <div className="text-center" style={{ padding:12 }}>
                    <div className="spinner" />
                </div>
            )}
            {!hasMore && items.length > 0 && (
                <div className="text-center" style={{ padding:12, color:'#666' }}> {pageRes['Items.NoMore'] ?? resources.get('Items.NoMore', 'אין עוד פריטים להצגה')} </div>
            )}
            {showLeadForm && (
                <div className="modal-overlay" onClick={() => closeModalAndGoToItemPage(goToItemId)}>
                    <div className="modal-content lead-modal" onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => closeModalAndGoToItemPage(goToItemId)}>×</button>
                        <LeadForm
                            showDiscountImage={true}
                            onSubmit={() => closeModalAndGoToItemPage(goToItemId)} // <-- close modal after submit
                        />
                    </div>
                </div>
            )}


        </div>
    );

}

export default Items;